/**
 * The one list of tracking params GLORB recognises.
 *
 * It used to live inline in hooks.server.js while first-touch.js kept its own
 * subset and the cart attributes accepted anything at all, so "which params do
 * we carry" had three different answers. Nothing here is server-only, so both
 * sides import it: hooks reads them off the URL, and the cart-attribute
 * allowlist is derived from it (see tracking-attributes.js).
 */

/**
 * Plain campaign labels (utm_source=meta and friends). Not personal data -
 * attached to the order as first-party metadata under legitimate interest, so
 * they travel regardless of consent.
 */
export const PLAIN_TRACKING_PARAMS = Object.freeze([
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  // Meta (Facebook/Instagram) ads attach non-standard params - GLORB's ad URLs
  // carry these instead of utm_source/medium/campaign, so paid-social had no
  // campaign attribution without them. utm_id = Meta campaign/ad id.
  "utm_placement",
  "utm_id",
  "utm_ad",
  "utm_adset",
  // Audience label (?aud=pro) - registerAttribution promotes it to a
  // first-class `audience` super property for ad-spend segmentation.
  "aud",
  "bg_ref",
  "affiliate",
  "aff",
  "partner",
  "sca_ref",
  "ref",
  "referrer",
  "source",
  "campaign",
  "medium",
  "discount",
]);

/**
 * Cross-context identifiers: an ad or email platform can match these back to
 * an individual, so they only attach with marketing consent, are never written
 * to the pre-consent first-touch cookie, and stay current-request-only.
 */
export const CONSENT_ONLY_PARAMS = Object.freeze([
  "fbclid",
  "gclid",
  "msclkid",
  "ttclid",
  "mc_cid",
  "mc_eid",
]);

/** Everything hooks.server.js captures off the landing URL. */
export const TRACKING_PARAMS = Object.freeze([
  ...PLAIN_TRACKING_PARAMS,
  ...CONSENT_ONLY_PARAMS,
]);

export const CONSENT_ONLY_PARAM_SET = Object.freeze(
  new Set(CONSENT_ONLY_PARAMS),
);
